/* IgelkottPlates collections — Eric 2026-05-11 (rev135).
 *
 * Foundation för Collections-feature (post-Eric:s designval):
 *   - Smart-filter datamodell (filter_state + added_handles + excluded_handles)
 *   - Read-only share (URL via share_token, v2)
 *   - Recent 5 i filter-meny + /account/collections för "all" (v2)
 *
 * MVP rev135 (denna fil):
 *   - State-mgmt (Map of id → collection)
 *   - localStorage MVP-persistence (server-sync via /apps/igelkott/collections
 *     i rev136)
 *   - Popup open/close + create-flow + add-to-collection-flow
 *   - Intercept klick på [data-feed-action="save"] för pre-existing
 *     plate-card Save-knappar (sektion igelkott-feed.liquid:524)
 *
 * Anonyma: openPopup() no-op:ar (popup-DOM finns inte enligt
 *   liquid {% if customer %}). Save-klick redirectas av existing
 *   feed.js-handler till login.
 */
(function () {
  'use strict';

  const log = (window.igelkott && window.igelkott.log) || {
    debug: () => {}, info: () => {},
    warn: console.warn.bind(console), error: console.error.bind(console),
  };
  /*
    Eric 2026-05-11 (rev141 hot-fix) — Telegram-alert visade
    window.unhandledrejection × 3 på path "/" från customer 5433342099652.
    Root cause: rev139 server-sync använde `fetchAuth(...)` men declared
    ALDRIG fetchAuth i denna fil (det fanns i demo-modal.js, separat IIFE-
    scope). ReferenceError vid varje pullServerSync()-anrop → mount-time
    unhandled-rejection på alla pages eftersom collections.js loadas
    via theme.liquid.

    Samma fetchAuth-pattern som demo-modal.js: prefer window.igelkott.fetch
    om existing (för App Proxy auth-handling), annars vanlig fetch.
  */
  const fetchAuth =
    (window.igelkott && window.igelkott.fetch) || window.fetch.bind(window);

  const STORAGE_KEY = 'igelkott.collections.v1';
  const MAX_COLLECTIONS = 20;
  const MAX_PLATES_PER_COLLECTION = 100;
  const MAX_NAME_LENGTH = 80;

  /*
    Eric 2026-05-11 (rev139) — Server-sync via /apps/igelkott/collections.
    Backend persistar i customer.metafields.igelkott.collections (json).
    Theme-side cachar i localStorage för instant-paint + offline-tolerans;
    GET vid mount → överrider localStorage om servern har annan version.
    PUT efter varje mutation → last-write-wins (single-customer-multi-tab
    edge-case accepteras).
  */
  const SYNC_GET_URL = '/apps/igelkott/collections';
  const SYNC_PUT_URL = '/apps/igelkott/collections';
  const SYNC_DEBOUNCE_MS = 800;
  let syncTimer = null;
  let syncInFlight = null;
  /*
    Eric 2026-05-11 (rev159) — pullServerSync race-skydd. Om user
    muterar local state (delete/rename/create/add-plate) MEDAN ett
    inflight GET /collections väntar på svar, ska pull-svaret INTE
    skriva över local state — annars återuppstår den raderade
    collection ("försvinner och kommer tillbaka"-bug).

    Vid varje saveToStorage() bumpas `localMutationSeq`. pullServerSync
    snappar en before-seq och jämför mot after-seq. Mismatch → discard
    pull-data och pusha local istället.
  */
  let localMutationSeq = 0;

  // ── State ──────────────────────────────────────────────────────────────
  // collections: Map<id, collection>
  // Format per Eric:s beslut #2 "smart filter":
  //   { id, name, slug, filter_state, added_handles, excluded_handles,
  //     cover_handle, share_token, created_at, updated_at }
  const collections = new Map();
  let popupOpenForHandle = null;
  // rev213 — trim som väntar på collection-val (sätts av openPopup opts)
  let popupPendingTrim = null;
  let popupLastFocused = null;
  /*
    Eric 2026-05-11 (rev158) — Emma-feedback: när user togglar en
    existerande collection känns det "stuck" att manuellt klicka X
    för att stänga. Lägg en auto-close timer (~1100ms) efter
    toggle-success så toast/✓ hinner synas och popupen stänger
    sig själv. Cancel om user gör annan interaktion (typing,
    create-toggle, ny toggle).
  */
  let autoCloseTimer = null;
  const AUTO_CLOSE_MS = 1100;

  // Customer data injectat av theme.liquid via window.igelkott.customer
  // (eller läses från customer.metafields.igelkott.collections när rev136
  // har metafield-schema satt). MVP: läs från localStorage.
  function loadFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      parsed.forEach((c) => {
        if (c && c.id) collections.set(c.id, c);
      });
      log.info('collections.loaded', { count: collections.size });
    } catch (err) {
      log.warn('collections.load_failed', { error: err.message });
    }
  }

  function saveToStorage() {
    // rev159 — bump seq INNAN write/sync så pull-race detekteras även
    // om pull-svaret kommer mellan localStorage.setItem och PUT-scheduling.
    localMutationSeq += 1;
    try {
      const arr = Array.from(collections.values());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch (err) {
      log.warn('collections.save_failed', { error: err.message });
    }
    scheduleServerSync();
  }

  /*
    Server-sync: debouncad PUT av hela collections-arrayen. Backend
    replace:ar hela metafield-värdet (last-write-wins). Vid 800ms
    inaktivitet efter sista mutation → PUT. Multipla rapid mutations
    coalesce:as till en enda request.
  */
  function scheduleServerSync() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(pushServerSync, SYNC_DEBOUNCE_MS);
  }

  async function pushServerSync() {
    if (syncInFlight) {
      // En request flyger redan; schemalägga ny efter completion
      try { await syncInFlight; } catch (_) {}
      scheduleServerSync();
      return;
    }
    const arr = Array.from(collections.values());
    const body = JSON.stringify({ collections: arr });
    syncInFlight = fetchAuth(SYNC_PUT_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body,
    }).then(async (r) => {
      if (!r.ok) {
        const text = await r.text().catch(() => '');
        log.warn('collections.sync_push_failed', { status: r.status, body: text.slice(0, 200) });
        return null;
      }
      const j = await r.json().catch(() => null);
      log.info('collections.sync_push_ok', { count: arr.length, updated_at: j && j.updated_at });
      return j;
    }).catch((err) => {
      log.warn('collections.sync_push_network', { error: err.message });
      return null;
    }).finally(() => {
      syncInFlight = null;
    });
  }

  async function pullServerSync() {
    /*
      Hämta senaste från servern och merge:a. Strategy: serverns array
      är source-of-truth om den finns. localStorage är cache för instant-
      paint men ersätts av server-data när tillgänglig.

      Anonyma users hoppar över (popup-DOM finns inte → modul är ändå
      bortroutad). Logged-in users som inte har metafield än (ny customer):
      backend returnerar tom array → vi behåller localStorage.

      Rev159 race-skydd: snappa localMutationSeq INNAN GET → om user
      muterat under flight → discard server-data och pusha local
      istället (annars återuppstår just-raderade collections).
    */
    const seqBefore = localMutationSeq;
    try {
      const r = await fetchAuth(SYNC_GET_URL, {
        method: 'GET',
        credentials: 'same-origin',
      });
      if (!r.ok) {
        log.debug('collections.sync_pull_skipped', { status: r.status });
        return;
      }
      const j = await r.json();
      if (!j || !Array.isArray(j.collections)) return;

      // Race-check: om local mutation skedde under flight → server-data är stale
      if (localMutationSeq !== seqBefore) {
        log.info('collections.sync_pull_stale_local_dirty', {
          seq_before: seqBefore, seq_after: localMutationSeq, server_count: j.collections.length,
        });
        scheduleServerSync();
        return;
      }

      if (j.collections.length === 0 && collections.size > 0) {
        // Server är tom, vi har localStorage-data → behåll och pusha upp
        log.info('collections.sync_pull_empty_pushing_local', { local_count: collections.size });
        scheduleServerSync();
        return;
      }
      // Server har data → ersätt in-memory + localStorage
      collections.clear();
      j.collections.forEach((c) => {
        if (c && c.id) collections.set(c.id, c);
      });
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(j.collections));
      } catch (_) {}
      log.info('collections.sync_pull_ok', { count: j.collections.length });
      document.dispatchEvent(new CustomEvent('igelkott-collections-changed', { detail: { reason: 'server_pull' } }));
    } catch (err) {
      log.warn('collections.sync_pull_network', { error: err.message });
    }
  }

  // ── ID generation ──────────────────────────────────────────────────────
  // Pseudo-ksuid: timestamp prefix (sortable) + random suffix.
  function generateId() {
    const ts = Date.now().toString(36);
    const rnd = Math.random().toString(36).slice(2, 8);
    return `c_${ts}_${rnd}`;
  }

  function slugify(name) {
    return (name || '')
      .toLowerCase()
      .replace(/[åä]/g, 'a').replace(/ö/g, 'o')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'collection';
  }

  // ── CRUD ───────────────────────────────────────────────────────────────
  function getCollections() {
    return Array.from(collections.values()).sort(
      (a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')
    );
  }

  function getRecentCollections(n = 5) {
    return getCollections().slice(0, n);
  }

  function createCollection(name) {
    if (!name || !name.trim()) throw new Error('Collection name is required');
    name = name.trim().slice(0, MAX_NAME_LENGTH);
    if (collections.size >= MAX_COLLECTIONS) {
      throw new Error(`You have reached the maximum of ${MAX_COLLECTIONS} collections. Delete one to create a new.`);
    }
    const id = generateId();
    const now = new Date().toISOString();
    const collection = {
      id,
      name,
      slug: slugify(name),
      filter_state: {},
      added_handles: [],
      excluded_handles: [],
      cover_handle: null,
      share_token: null,
      created_at: now,
      updated_at: now,
    };
    collections.set(id, collection);
    saveToStorage();
    log.info('collections.created', { id, name });
    document.dispatchEvent(new CustomEvent('igelkott-collections-changed', { detail: { reason: 'created', id } }));
    return collection;
  }

  /*
    Eric 2026-05-11 (rev159) — proper delete med server-sync.
    Tidigare hack i collections-page.js skrev bara localStorage + reload,
    vilket race-condition:ade mot pullServerSync (server returnerade
    fortfarande den raderade collection → mergades in igen → "card
    försvinner och kommer tillbaka"-bug Emma+Eric rapporterade).

    Nu: ta bort från in-memory Map → saveToStorage() (skriver localStorage
    + debouncad PUT som speglar deletion till backend metafield).
  */
  function deleteCollection(collectionId) {
    const c = collections.get(collectionId);
    if (!c) return false;
    collections.delete(collectionId);
    saveToStorage();
    log.info('collections.deleted', { id: collectionId, name: c.name });
    document.dispatchEvent(new CustomEvent('igelkott-collections-changed', { detail: { reason: 'deleted', id: collectionId } }));
    return true;
  }

  /*
    Eric 2026-05-11 (rev160) — memoizea share_token från server-POST.
    Tidigare hack i collections-page.js skrev direkt localStorage utan
    att gå via saveToStorage → server-PUT triggades aldrig → metafield
    fick aldrig tokenet → vid reload tappade in-memory tokenet → nästa
    Share-klick gjorde ny POST (idempotent backend räddade UX men onödig
    trafik + inkonsistens).
  */
  function setShareToken(collectionId, token) {
    const c = collections.get(collectionId);
    if (!c) return null;
    if (c.share_token === token) return c; // no-op
    c.share_token = token || null;
    c.updated_at = new Date().toISOString();
    saveToStorage();
    log.info('collections.share_token_set', { id: collectionId, has_token: !!token });
    return c;
  }

  function renameCollection(collectionId, newName) {
    const c = collections.get(collectionId);
    if (!c) throw new Error('Collection not found');
    const name = (newName || '').trim();
    if (!name) throw new Error('Name cannot be empty');
    if (name.length > 80) throw new Error('Name too long (max 80 chars)');
    c.name = name;
    c.slug = slugify(name);
    c.updated_at = new Date().toISOString();
    saveToStorage();
    log.info('collections.renamed', { id: collectionId, name });
    document.dispatchEvent(new CustomEvent('igelkott-collections-changed', { detail: { reason: 'renamed', id: collectionId } }));
    return c;
  }

  /*
    Eric 2026-07-20 (rev213) — trim per (collection, handle). Samma plate
    kan ha OLIKA trim i olika collections. Trim skapas bara via "spara
    till collection"-flödet (tvingande — en trim kan inte existera utanför
    en collection). Shape: c.trims = { "<handle>": { in_s, out_s } }.
    Äldre collections saknar fältet → ensureTrims() normaliserar lazy.
  */
  function ensureTrims(c) {
    if (!c.trims || typeof c.trims !== 'object' || Array.isArray(c.trims)) {
      c.trims = {};
    }
    return c.trims;
  }

  function setPlateTrim(collectionId, handle, trim) {
    const c = collections.get(collectionId);
    if (!c) throw new Error('Collection not found');
    if (!handle) throw new Error('Plate handle required');
    if (!c.added_handles.includes(handle)) {
      throw new Error('Plate must be in the collection to have a trim');
    }
    if (!trim || typeof trim.in_s !== 'number' || typeof trim.out_s !== 'number'
        || trim.out_s <= trim.in_s || trim.in_s < 0) {
      throw new Error('Invalid trim range');
    }
    ensureTrims(c)[handle] = { in_s: trim.in_s, out_s: trim.out_s };
    c.updated_at = new Date().toISOString();
    saveToStorage();
    log.info('collections.trim_set', {
      id: collectionId, handle, in_s: trim.in_s, out_s: trim.out_s,
    });
    document.dispatchEvent(new CustomEvent('igelkott-collections-changed', {
      detail: { reason: 'trim_set', id: collectionId, handle },
    }));
    return c;
  }

  function getPlateTrim(collectionId, handle) {
    const c = collections.get(collectionId);
    if (!c || !c.trims) return null;
    const t = c.trims[handle];
    return (t && typeof t.in_s === 'number' && typeof t.out_s === 'number') ? t : null;
  }

  function clearPlateTrim(collectionId, handle) {
    const c = collections.get(collectionId);
    if (!c || !c.trims || !(handle in c.trims)) return;
    delete c.trims[handle];
    c.updated_at = new Date().toISOString();
    saveToStorage();
    log.info('collections.trim_cleared', { id: collectionId, handle });
    document.dispatchEvent(new CustomEvent('igelkott-collections-changed', {
      detail: { reason: 'trim_cleared', id: collectionId, handle },
    }));
  }

  // mm:ss-format för trim-badges (plates är < 1h, timmar behövs inte)
  function formatTrimTime(s) {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return m + ':' + String(sec).padStart(2, '0');
  }

  function addPlateToCollection(collectionId, handle, trim) {
    const c = collections.get(collectionId);
    if (!c) throw new Error('Collection not found');
    if (!handle) throw new Error('Plate handle required');
    if (c.added_handles.includes(handle)) {
      // rev213 — redan i collection: om trim medföljer, uppdatera den
      // (samma plate, ny/ändrad range). Utan trim: no-op som tidigare.
      if (trim) return setPlateTrim(collectionId, handle, trim);
      return c;
    }
    if (c.added_handles.length >= MAX_PLATES_PER_COLLECTION) {
      throw new Error(`Collection "${c.name}" already has ${MAX_PLATES_PER_COLLECTION} plates (max).`);
    }
    c.added_handles.push(handle);
    // Remove from excluded if previously excluded
    c.excluded_handles = c.excluded_handles.filter((h) => h !== handle);
    if (!c.cover_handle) c.cover_handle = handle;
    if (trim && typeof trim.in_s === 'number' && typeof trim.out_s === 'number'
        && trim.out_s > trim.in_s && trim.in_s >= 0) {
      ensureTrims(c)[handle] = { in_s: trim.in_s, out_s: trim.out_s };
    }
    c.updated_at = new Date().toISOString();
    saveToStorage();
    log.info('collections.plate_added', {
      id: collectionId, handle, with_trim: !!trim,
    });
    document.dispatchEvent(new CustomEvent('igelkott-collections-changed', { detail: { reason: 'plate_added', id: collectionId, handle } }));
    return c;
  }

  function removePlateFromCollection(collectionId, handle) {
    const c = collections.get(collectionId);
    if (!c) return null;
    c.added_handles = c.added_handles.filter((h) => h !== handle);
    // rev213 — trim följer platen: bort ur collection → bort med trim
    if (c.trims && handle in c.trims) delete c.trims[handle];
    if (c.cover_handle === handle) {
      c.cover_handle = c.added_handles[0] || null;
    }
    c.updated_at = new Date().toISOString();
    saveToStorage();
    document.dispatchEvent(new CustomEvent('igelkott-collections-changed', { detail: { reason: 'plate_removed', id: collectionId, handle } }));
    return c;
  }

  function isPlateInCollection(collectionId, handle) {
    const c = collections.get(collectionId);
    if (!c) return false;
    return c.added_handles.includes(handle);
  }

  // ── Popup UI (only mounted för logged-in users) ───────────────────────
  /*
    Eric 2026-05-11 (rev155) — anonyma users (popup-DOM saknas) ska ändå
    få URL-state-filter applicering så `?shared=<token>` och
    `?collection=<id>` fungerar i incognito + utan login. Tidigare
    early-return:ade hela modulen → applyUrlStateFilter kördes aldrig.
    Nu: hoppa över popup-init men kör resten (URL-filter, feed.js-bridge).
  */
  const popup = document.getElementById('igelkott-collection-popup');
  const isAnonymous = !popup;

  const listEl = popup ? popup.querySelector('[data-collection-list]') : null;
  const emptyEl = popup ? popup.querySelector('[data-collection-empty]') : null;
  const plateInfoEl = popup ? popup.querySelector('[data-collection-plate-info]') : null;
  const plateHandleEl = popup ? popup.querySelector('[data-collection-plate-handle]') : null;
  const createToggleBtn = popup ? popup.querySelector('[data-collection-create-toggle]') : null;
  const createFormEl = popup ? popup.querySelector('[data-collection-create-form]') : null;
  const newNameInput = popup ? popup.querySelector('[data-collection-new-name]') : null;
  const newNameCount = popup ? popup.querySelector('[data-collection-new-name-count]') : null;
  const createSubmitBtn = popup ? popup.querySelector('[data-collection-create-submit]') : null;
  const createCancelBtn = popup ? popup.querySelector('[data-collection-create-cancel]') : null;
  const statusEl = popup ? popup.querySelector('[data-collection-status]') : null;
  const closeEls = popup ? popup.querySelectorAll('[data-collection-close]') : [];

  function renderList() {
    if (!listEl) return;
    listEl.innerHTML = '';
    const all = getCollections();
    if (all.length === 0) {
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    all.forEach((c) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      const isChecked = popupOpenForHandle && isPlateInCollection(c.id, popupOpenForHandle);
      li.setAttribute('aria-checked', isChecked ? 'true' : 'false');
      // rev213 — visa befintlig trim för den här platen i den här
      // collection:en (per-collection-trims: kan skilja mellan rader).
      const existingTrim = popupOpenForHandle ? getPlateTrim(c.id, popupOpenForHandle) : null;
      const trimBadge = existingTrim
        ? `<span class="igelkott-collection-popup__item-trim">${formatTrimTime(existingTrim.in_s)}–${formatTrimTime(existingTrim.out_s)}</span>`
        : '';
      li.innerHTML = `
        <button type="button" class="igelkott-collection-popup__item" aria-checked="${isChecked}" data-collection-id="${c.id}">
          <span class="igelkott-collection-popup__item-checkbox" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </span>
          <span class="igelkott-collection-popup__item-name"></span>
          ${trimBadge}
          <span class="igelkott-collection-popup__item-meta">${c.added_handles.length} plate${c.added_handles.length === 1 ? '' : 's'}</span>
        </button>
      `;
      // Avoid innerHTML for user-supplied name (XSS-safe)
      li.querySelector('.igelkott-collection-popup__item-name').textContent = c.name;
      listEl.appendChild(li);
    });
  }

  function setStatus(text, tone) {
    if (!statusEl) return;
    if (!text) {
      statusEl.hidden = true;
      return;
    }
    statusEl.textContent = text;
    statusEl.dataset.tone = tone || 'ok';
    statusEl.hidden = false;
  }

  function openPopup(handle, opts) {
    if (!popup) {
      // Anonym user — redirect till login
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/customer_authentication/login?return_to=${returnTo}`;
      return;
    }
    cancelAutoClose();
    popupOpenForHandle = handle || null;
    /*
      rev213 — pendingTrim: när trim-strippen på produktsidan öppnar
      popupen medföljer {in_s, out_s}. Trim persisteras FÖRST när user
      väljer en collection (tvingande koppling trim ↔ collection).
      Stängd popup utan val = trim förblir opersisterad (kvar som
      pending på strippen).
    */
    popupPendingTrim = (opts && opts.trim
      && typeof opts.trim.in_s === 'number' && typeof opts.trim.out_s === 'number'
      && opts.trim.out_s > opts.trim.in_s)
      ? { in_s: opts.trim.in_s, out_s: opts.trim.out_s }
      : null;
    popupLastFocused = document.activeElement;
    if (plateInfoEl && plateHandleEl) {
      if (handle) {
        plateHandleEl.textContent = popupPendingTrim
          ? `${handle} · trim ${formatTrimTime(popupPendingTrim.in_s)}–${formatTrimTime(popupPendingTrim.out_s)}`
          : handle;
        plateInfoEl.hidden = false;
      } else {
        plateInfoEl.hidden = true;
      }
    }
    setStatus(null);
    resetCreateForm();
    renderList();
    popup.hidden = false;
    popup.removeAttribute('inert');
    popup.setAttribute('aria-modal', 'true');
    requestAnimationFrame(() => {
      popup.dataset.state = 'open';
      popup.setAttribute('aria-hidden', 'false');
    });
    document.body.style.overflow = 'hidden';
    // Focus first interactive
    setTimeout(() => {
      const firstItem = listEl && listEl.querySelector('button[data-collection-id]');
      const focusTarget = firstItem || createToggleBtn;
      if (focusTarget) focusTarget.focus();
    }, 100);
    log.info('collections.popup_opened', { handle, collections_count: collections.size });
  }

  function closePopup() {
    if (!popup) return;
    cancelAutoClose();
    popup.dataset.state = 'closed';
    popup.setAttribute('aria-hidden', 'true');
    popup.setAttribute('aria-modal', 'false');
    popup.setAttribute('inert', '');
    document.body.style.overflow = '';
    if (popupLastFocused && typeof popupLastFocused.focus === 'function') {
      try { popupLastFocused.focus(); } catch (_) {}
    }
    setTimeout(() => {
      popup.hidden = true;
      popupOpenForHandle = null;
      popupPendingTrim = null;
      resetCreateForm();
    }, 220);
  }

  function scheduleAutoClose() {
    cancelAutoClose();
    autoCloseTimer = setTimeout(() => {
      autoCloseTimer = null;
      if (popup && popup.dataset.state === 'open') closePopup();
    }, AUTO_CLOSE_MS);
  }

  function cancelAutoClose() {
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }
  }

  function resetCreateForm() {
    if (createFormEl) createFormEl.hidden = true;
    if (createToggleBtn) createToggleBtn.hidden = false;
    if (newNameInput) newNameInput.value = '';
    if (newNameCount) newNameCount.textContent = '0';
    if (createSubmitBtn) createSubmitBtn.disabled = true;
  }

  function expandCreateForm() {
    // rev158 — user vill skapa ny collection, avbryt pending auto-close
    cancelAutoClose();
    if (createFormEl) createFormEl.hidden = false;
    if (createToggleBtn) createToggleBtn.hidden = true;
    if (newNameInput) {
      setTimeout(() => newNameInput.focus(), 50);
    }
  }

  // ── Event wiring (popup-bindings hoppas för anonyma) ──────────────────
  if (!isAnonymous) {
    closeEls.forEach((el) => el.addEventListener('click', closePopup));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.dataset.state === 'open') closePopup();
    });
  }

  if (createToggleBtn) {
    createToggleBtn.addEventListener('click', expandCreateForm);
  }

  if (createCancelBtn) {
    createCancelBtn.addEventListener('click', resetCreateForm);
  }

  if (newNameInput) {
    newNameInput.addEventListener('input', () => {
      // rev158 — typing = aktiv interaktion, avbryt pending auto-close
      cancelAutoClose();
      const v = newNameInput.value || '';
      if (newNameCount) newNameCount.textContent = String(v.length);
      if (createSubmitBtn) createSubmitBtn.disabled = !v.trim();
    });
    newNameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && newNameInput.value.trim()) {
        e.preventDefault();
        handleCreateSubmit();
      }
    });
  }

  if (createSubmitBtn) {
    createSubmitBtn.addEventListener('click', handleCreateSubmit);
  }

  function handleCreateSubmit() {
    const name = (newNameInput && newNameInput.value || '').trim();
    if (!name) return;
    try {
      const c = createCollection(name);
      if (popupOpenForHandle) {
        addPlateToCollection(c.id, popupOpenForHandle, popupPendingTrim || undefined);
        setStatus(popupPendingTrim
          ? `Created "${c.name}" and saved the trim.`
          : `Created "${c.name}" and added the plate.`, 'ok');
      } else {
        setStatus(`Created collection "${c.name}".`, 'ok');
      }
      resetCreateForm();
      renderList();
      // TODO rev136: POST /apps/igelkott/collections för server-sync
      setTimeout(closePopup, 1200);
    } catch (err) {
      setStatus(err.message, 'err');
    }
  }

  // Click on collection-item toggles plate in/out
  if (listEl) {
    listEl.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-collection-id]');
      if (!btn) return;
      const id = btn.dataset.collectionId;
      if (!id || !popupOpenForHandle) return;
      try {
        if (isPlateInCollection(id, popupOpenForHandle)) {
          /*
            rev213 — med pendingTrim betyder klick på en collection som
            redan har platen "uppdatera trimmen här", inte "ta bort".
            Utan pendingTrim: klassiskt toggle-remove.
          */
          if (popupPendingTrim) {
            const c = setPlateTrim(id, popupOpenForHandle, popupPendingTrim);
            setStatus(`Trim updated in "${c.name}".`, 'ok');
          } else {
            removePlateFromCollection(id, popupOpenForHandle);
            setStatus('Removed from collection.', 'ok');
          }
        } else {
          const c = addPlateToCollection(id, popupOpenForHandle, popupPendingTrim || undefined);
          setStatus(popupPendingTrim
            ? `Saved to "${c.name}" with trim.`
            : `Added to "${c.name}".`, 'ok');
        }
        renderList();
        /*
          Eric 2026-05-11 (rev158) — auto-close popup efter toggle.
          Emma-feedback: "It feels disruptive to just hit x". Visa
          toast → vänta 1.1s → stäng. Om user togglar igen / börjar
          typea i create-form, resettas/avbryts timern.
        */
        scheduleAutoClose();
      } catch (err) {
        setStatus(err.message, 'err');
        cancelAutoClose();
      }
    });
  }

  // Backdrop click closes (popup-only)
  if (popup) {
    popup.addEventListener('click', (e) => {
      if (e.target === popup) closePopup();
    });
  }

  // ── Save-button intercept ──────────────────────────────────────────────
  // Existing feed.js har en click-handler på [data-feed-action="save"]
  // som persistar localSaved-state. Vi interceptar via capture-phase
  // INNAN feed.js-handlern och öppnar popup istället. Capture med
  // `stopPropagation` förhindrar feed.js-handlern att köra om vi
  // valde "open popup"-flödet.
  //
  // Existing feed.js Save-flow (single-click) blir nu en collection-
  // entry-point. För backwards-compat (anonyma users + no-popup-mode)
  // kan vi senare lägga en preferens.
  document.addEventListener('click', (e) => {
    /*
      Eric 2026-05-11 (rev138 fix) — fånga både feed-card Save
      (data-feed-action="save") OCH product-page Save
      (data-product-action="save"). Båda triggar Collections-popup.
      stopImmediatePropagation:ar för att blocka existing bubble-phase
      handlers (legacy local-save-toggle) eftersom Collections nu är
      det enda paradigmet för "save plate".
    */
    const btn = e.target.closest('[data-feed-action="save"], [data-product-action="save"]');
    if (!btn) return;
    // Find plate handle from closest card / product-root
    const card = btn.closest('[data-handle], [data-feed-card], [data-product-root]');
    const handle = card ? (card.dataset.handle || '') : '';
    if (!handle) {
      log.debug('collections.save_intercept_no_handle');
      return; // Låt original handler köra
    }
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    openPopup(handle);
  }, true); // capture-phase — körs INNAN bubble-phase handlers

  // ── Filter-menu integration (rev137) ───────────────────────────────────
  /*
    Rendera recent 5 collections i header-filter-dropdown:
      <div data-collections-menu-slot>
        [empty-state om 0 collections]
        [collection-item × N]
      </div>

    Vid klick på en collection-item: sätt activeUserFilters med
    'collection:<id>'-token (existing handler i feed.js måste utökas
    för att hantera detta — gjort i samma rev). aria-checked syncas
    mot active state.
  */
  const RECENT_LIMIT = 5;
  const ACTIVE_COLLECTION_KEY = 'igelkott.collections.active_filter';

  function getActiveCollectionFilter() {
    try { return sessionStorage.getItem(ACTIVE_COLLECTION_KEY) || null; }
    catch (_) { return null; }
  }

  function setActiveCollectionFilter(collectionId) {
    try {
      if (collectionId) {
        sessionStorage.setItem(ACTIVE_COLLECTION_KEY, collectionId);
      } else {
        sessionStorage.removeItem(ACTIVE_COLLECTION_KEY);
      }
    } catch (_) {}
  }

  function renderCollectionsInMenu() {
    const slot = document.querySelector('[data-collections-menu-slot]');
    if (!slot) return;
    const recent = getRecentCollections(RECENT_LIMIT);
    const emptyEl = slot.querySelector('[data-collections-empty]');
    const activeFilterId = getActiveCollectionFilter();

    /*
      Eric 2026-05-11 (rev161) — cleanup måste matcha rev156-strukturen.
      Före rev156: varje rad var en <button.igelkott-header__collection-item>.
      Efter rev156: varje rad är <div.igelkott-header__collection-row>
        som innehåller item-knappen OCH en copy-knapp.
      Bug: cleanup tog bort bara item-knappen → wrapper + copy-knapp
      ackumulerades vid varje re-render → "extra copy knapp varje plate
      jag sparar"-bug Eric rapporterade.
      Fix: ta bort hela .igelkott-header__collection-row-wrappern.
    */
    if (recent.length === 0) {
      if (emptyEl) emptyEl.style.display = '';
      slot.querySelectorAll('.igelkott-header__collection-row').forEach((el) => el.remove());
      return;
    }
    if (emptyEl) emptyEl.style.display = 'none';

    // Remove existing rows + rebuild
    slot.querySelectorAll('.igelkott-header__collection-row').forEach((el) => el.remove());

    recent.forEach((c) => {
      /*
        Eric 2026-05-11 (rev156) — wrapper med två button-children:
        (1) main filter-toggle (klick = aktivera/avaktivera collection-
            filter), (2) copy-share-link icon button (klick = POST /share
            om token saknas, kopiera URL till clipboard, visa toast).
      */
      const wrap = document.createElement('div');
      wrap.className = 'igelkott-header__collection-row';
      wrap.dataset.collectionId = c.id;

      const isActive = activeFilterId === c.id;
      /*
        Eric 2026-05-11 (rev163) — count-span borttagen per request:
        "hur många klipp det är i, den siffran kan du ta bort".
        Namn-spannet får hela row-bredden (utöver bookmark-ikon +
        copy-knapp) och ellipsis:ar via CSS (rad 987-989).
      */
      wrap.innerHTML = `
        <button type="button" class="igelkott-header__collection-item" role="menuitemradio" aria-checked="${isActive}" data-collection-id="${c.id}" data-collection-action="filter">
          <svg class="igelkott-header__collection-item-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
          </svg>
          <span class="igelkott-header__collection-item-name"></span>
        </button>
        <button type="button" class="igelkott-header__collection-copy" data-collection-id="${c.id}" data-collection-action="copy" aria-label="Copy share link" title="Copy share link">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
      `;
      wrap.querySelector('.igelkott-header__collection-item-name').textContent = c.name;
      slot.appendChild(wrap);
    });
  }

  // ── Copy-share-link i filter-meny (rev156) ─────────────────────────────
  async function copyCollectionShareLink(collectionId, btnEl) {
    const c = collections.get(collectionId);
    if (!c) return;

    let token = c.share_token;
    if (!token) {
      // Skapa share-token via backend
      try {
        const r = await fetchAuth('/apps/igelkott/collections/share', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ collection_id: collectionId }),
        });
        if (!r.ok) {
          log.error('collections.menu_share_failed', { status: r.status });
          flashCopyState(btnEl, false);
          return;
        }
        const j = await r.json();
        token = j.token;
        // rev160 — gå via setShareToken för konsistens med share-modal-pathen
        setShareToken(collectionId, token);
      } catch (err) {
        log.error('collections.menu_share_network', { error: err.message });
        flashCopyState(btnEl, false);
        return;
      }
    }
    /*
      Eric 2026-05-11 (rev162) — peka share-länken till proxy-endpoint
      /apps/igelkott/share/<token> istället för /pages/collection?t=<token>.
      Proxy returnerar HTML med collection-specifika Open Graph-tags
      (name + cover_image) så WhatsApp/Slack/iMessage-link-preview
      speglar den faktiska collection som delas. Faktiska users
      redirectas client-side till /pages/collection?t=<token>
      (befintlig read-only-vy oförändrad).
    */
    const url = `${window.location.origin}/apps/igelkott/share/${encodeURIComponent(token)}`;
    try {
      await navigator.clipboard.writeText(url);
      flashCopyState(btnEl, true);
      log.info('collections.menu_share_copied', { id: collectionId });
    } catch (err) {
      // Fallback: focus + execCommand
      const tmp = document.createElement('textarea');
      tmp.value = url;
      document.body.appendChild(tmp);
      tmp.select();
      try { document.execCommand('copy'); flashCopyState(btnEl, true); }
      catch (_) { flashCopyState(btnEl, false); }
      tmp.remove();
    }
  }

  function flashCopyState(btnEl, ok) {
    if (!btnEl) return;
    btnEl.classList.toggle('is-copied', !!ok);
    btnEl.classList.toggle('is-err', !ok);
    setTimeout(() => {
      btnEl.classList.remove('is-copied', 'is-err');
    }, 1500);
  }

  // Click-handler för collection-items + copy-button i filter-menyn
  document.addEventListener('click', (e) => {
    // Copy-button (rev156) — hög prio: stop propagation så filter-toggle
    // inte triggar samtidigt
    const copyBtn = e.target.closest('.igelkott-header__collection-copy');
    if (copyBtn) {
      e.preventDefault();
      e.stopPropagation();
      copyCollectionShareLink(copyBtn.dataset.collectionId, copyBtn);
      return;
    }

    const btn = e.target.closest('.igelkott-header__collection-item');
    if (!btn) return;
    const id = btn.dataset.collectionId;
    if (!id) return;
    const currentActive = getActiveCollectionFilter();
    const willActivate = currentActive !== id;

    /*
      Eric 2026-05-12 (rev173) — single-active-filter-enforcement.
      När user aktiverar en collection → rensa likes/downloads i
      header (annars stannar de visuellt aktiva men kommer inte med
      i activeUserFilters, vilket gör state out-of-sync).
    */
    if (willActivate && window.igelkott
        && typeof window.igelkott.clearHeaderUserFilters === 'function') {
      window.igelkott.clearHeaderUserFilters();
    }

    setActiveCollectionFilter(willActivate ? id : null);

    const newFilters = new Set();
    if (willActivate) newFilters.add('collection:' + id);
    document.dispatchEvent(new CustomEvent('igelkott-filter-changed', {
      detail: { filters: newFilters },
    }));

    document.querySelectorAll('.igelkott-header__collection-item').forEach((it) => {
      it.setAttribute('aria-checked', it.dataset.collectionId === (willActivate ? id : '__none__') ? 'true' : 'false');
    });
    log.info('collections.menu_filter', { id, active: willActivate });
  });

  /*
    rev173 — clearActiveFilter public API: rensar active collection-
    filter + aria-checked på alla collection-items, utan att
    dispatcha igelkott-filter-changed (caller är ansvarig för att
    dispatcha rätt resulting state).
  */
  function clearActiveCollectionFilter() {
    setActiveCollectionFilter(null);
    document.querySelectorAll('.igelkott-header__collection-item').forEach((it) => {
      it.setAttribute('aria-checked', 'false');
    });
  }

  // Re-render menu when collections change (created / plate-added / removed)
  const origCreate = createCollection;
  const origAddPlate = addPlateToCollection;
  const origRemovePlate = removePlateFromCollection;
  // Wrap functions so menu re-renders. (We can't reassign const above;
  // istället hook via Object.defineProperty på public API.)

  /*
    Eric 2026-05-11 (rev145) — URL-state-application vid mount.
    Stöd för deep-links:
      /?collection=<id>     — applicera local collection-filter
      /?shared=<token>      — fetcha shared-snapshot + applicera ephemeral
                              plate-set-filter
      /?filter=liked        — (B2) standard user-filter via URL
      /?tod=night           — (B2) Day/Night-filter via URL
    Sätter activeUserFilters / activeTod att matcha URL-state, sedan
    fyrar igelkott-filter-changed-event så feed.js applicerar.
  */
  const _sharedPlates = new Set();
  /*
    Eric 2026-05-12 (rev183) — cacha shared-collection metadata
    (name, plate_count, token) så renderActiveFilterHeader i feed.js
    kan visa `Shared collection: "<name>"` istället för generic
    "Shared collection". Krävs också för anonyma users som inte har
    customer.metafields att läsa namnet från.
  */
  const _sharedMeta = { name: null, plate_count: 0, token: null };
  /*
    rev214 (Eric 2026-07-24) — "collections ska gå att ta del av 100%
    utan konto. trim är del av det."

    _sharedTrims: ägarens trim-ranges per handle från share-snapshoten.
    sessionStorage-persist av HELA shared-kontexten: _sharedPlates
    fylldes tidigare bara på feed-sidan (via ?shared=<token>) och dog
    vid navigation — produktsidor hade ingen aning om shared-kontexten.
    Nu överlever den page-navigations inom fliken så anonyma besökare
    ser ägarens markerade segment (trim-strip + range-lock) när de
    klickar sig in på en plate från en delad collection.
    sessionStorage (inte localStorage): shared-kontext är besöks-scoped,
    ska inte spöka kvar dagar senare.
  */
  const SHARED_CTX_KEY = 'igelkott.shared.ctx.v1';
  const _sharedTrims = {};

  function persistSharedCtx() {
    try {
      sessionStorage.setItem(SHARED_CTX_KEY, JSON.stringify({
        token: _sharedMeta.token,
        name: _sharedMeta.name,
        plates: Array.from(_sharedPlates),
        trims: _sharedTrims,
      }));
    } catch (_) {}
  }

  function restoreSharedCtx() {
    try {
      const raw = sessionStorage.getItem(SHARED_CTX_KEY);
      if (!raw) return;
      const ctx = JSON.parse(raw);
      if (!ctx || !Array.isArray(ctx.plates)) return;
      ctx.plates.forEach((h) => _sharedPlates.add(h));
      Object.assign(_sharedTrims, ctx.trims || {});
      _sharedMeta.token = ctx.token || null;
      _sharedMeta.name = ctx.name || null;
      _sharedMeta.plate_count = _sharedPlates.size;
    } catch (_) {}
  }
  // Restore direkt vid module-load — körs på ALLA sidor (product inkl.)
  restoreSharedCtx();

  function sharedPlatesIncludes(handle) {
    return _sharedPlates.has(handle);
  }
  function getSharedTrim(handle) {
    const t = _sharedTrims[handle];
    return (t && typeof t.in_s === 'number' && typeof t.out_s === 'number'
      && t.out_s > t.in_s) ? t : null;
  }
  function getSharedMeta() {
    return { ..._sharedMeta };
  }

  async function applyUrlStateFilter() {
    const params = new URLSearchParams(window.location.search);
    const sharedToken = (params.get('shared') || '').trim();
    const collectionId = (params.get('collection') || '').trim();
    const filterParam = (params.get('filter') || '').trim();
    const todParam = (params.get('tod') || '').trim();

    if (!sharedToken && !collectionId && !filterParam && !todParam) return;

    /*
      Eric 2026-05-11 (rev151) — fix för "klick på collection-card hamna på
      start utan filter". Race-condition: vid mount fyrade
      `igelkott-filter-changed` event före (a) feed.js's lyssnare
      attach:ats via defer-script-load OCH (b) pullServerSync() har
      populerat in-memory collections-Map. Resultat: event tappades
      eller userFilterMatches såg tom state → 0 träffar.

      Fix:
      1. Vänta tills pullServerSync() är klar (kollar collections.size
         eller deadline 3s)
      2. Vänta tills feed.js har data-feed-cards-container synlig
         i DOM (proxy för att listener är attached)
      3. Sen dispatcha filter-event
    */
    const filters = new Set();

    if (sharedToken) {
      try {
        const r = await fetchAuth(
          `/apps/igelkott/collections/shared/${encodeURIComponent(sharedToken)}`,
          { method: 'GET', credentials: 'same-origin' }
        );
        if (r.ok) {
          const j = await r.json();
          _sharedPlates.clear();
          (j.plates || []).forEach((h) => _sharedPlates.add(h));
          // rev183 — cacha meta för filter-rubrik (visar namnet,
          // funkar för anonyma users som inte har customer-data)
          _sharedMeta.name = j.name || null;
          _sharedMeta.plate_count = _sharedPlates.size;
          _sharedMeta.token = sharedToken;
          // rev214 — trims från snapshoten + persist så produktsidor
          // (utan ?shared=-param) har kontexten inom fliken
          Object.keys(_sharedTrims).forEach((k) => delete _sharedTrims[k]);
          Object.assign(_sharedTrims, j.trims || {});
          persistSharedCtx();
          filters.add('shared:' + sharedToken);
          log.info('url_filter.shared_applied', {
            count: _sharedPlates.size,
            name: j.name,
            trims_count: Object.keys(_sharedTrims).length,
          });
        } else {
          log.warn('url_filter.shared_fetch_failed', { status: r.status });
        }
      } catch (err) {
        log.warn('url_filter.shared_network', { error: err.message });
      }
    }

    if (collectionId && !sharedToken) {
      // Vänta tills in-memory state har denna collection (pullServerSync
      // pågår parallellt) ELLER 3s deadline.
      const deadline = Date.now() + 3000;
      while (Date.now() < deadline) {
        if (collections.has(collectionId)) break;
        await new Promise((r) => setTimeout(r, 100));
      }
      filters.add('collection:' + collectionId);
      setActiveCollectionFilter(collectionId);
    }

    if (filterParam) {
      filterParam.split(',').forEach((f) => {
        const trimmed = f.trim().toLowerCase();
        if (['liked', 'saved', 'purchased'].includes(trimmed)) {
          filters.add(trimmed);
        }
      });
    }

    if (filters.size > 0) {
      // Vänta tills feed.js är init (data-feed-cards i DOM) innan dispatch
      const feedDeadline = Date.now() + 3000;
      while (Date.now() < feedDeadline) {
        if (document.querySelector('[data-feed-cards]')) break;
        await new Promise((r) => setTimeout(r, 50));
      }
      log.info('url_filter.dispatching', { filters: Array.from(filters) });
      document.dispatchEvent(
        new CustomEvent('igelkott-filter-changed', { detail: { filters } })
      );

      /*
        Eric 2026-05-26 (rev207) — Safari incognito race-condition fix.
        WhatsApp-feedback från Emma: filter applicerar inte konsekvent
        på shared-URL i Safari incognito ("it shows me everything here").
        Refresh löste — symptom på race mellan event-dispatch och
        feed.js-listener-attach.

        Existerande deadline-wait (rad 977-981) kollar `[data-feed-cards]`-
        existens men inte att `igelkott-filter-changed`-listener faktiskt
        är registrerad. Safari incognito kan trigga `load`-event i
        annan ordning än Chrome, så listener kan attachas EFTER vår
        första dispatch.

        Fix: idempotent re-dispatch efter 500ms + 1500ms. Om feed:n
        redan applicerade filter:n är re-dispatch no-op (samma filter,
        samma cards). Om feed missade första dispatch — andra/tredje
        försök fångar listener nu attached. Inga visual flickers
        eftersom event-handler bara uppdaterar activeUserFilters +
        re-filtrar — om state är samma → render idempotent.
      */
      const retryDispatch = () => {
        log.info('url_filter.redispatching', { filters: Array.from(filters) });
        document.dispatchEvent(
          new CustomEvent('igelkott-filter-changed', { detail: { filters } })
        );
      };
      setTimeout(retryDispatch, 500);
      setTimeout(retryDispatch, 1500);
    }

    if (todParam && ['day', 'night'].includes(todParam.toLowerCase())) {
      const todBtn = document.querySelector(`[data-feed-tod="${todParam.toLowerCase()}"]`);
      if (todBtn) {
        setTimeout(() => todBtn.click(), 100);
      }
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────
  /*
    Eric 2026-05-11 (rev155) — anonyma users får också public API
    (för URL-filter-applicering + feed.js shared-plate-lookup), bara
    popup-relaterade funktioner är reduced till login-redirect.
    Server-sync + collections-storage hoppas för anonyma (de har inga
    persistent collections).
  */
  if (!isAnonymous) {
    loadFromStorage();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', renderCollectionsInMenu);
    } else {
      renderCollectionsInMenu();
    }
    document.addEventListener('igelkott-collections-changed', renderCollectionsInMenu);
    pullServerSync();
  }

  window.igelkott = window.igelkott || {};
  window.igelkott.collections = {
    isAnonymous,
    openCollectionPopup: openPopup,
    closeCollectionPopup: closePopup,
    getCollections,
    getRecentCollections,
    createCollection,
    // Rev159: exponerad delete + rename för collections-page.js att
    // använda istället för localStorage-hack + reload (race-fix).
    deleteCollection,
    renameCollection,
    // Rev173: clear active collection-filter (single-active-enforcement
    // + ✕-knapp i feed active-filter-header)
    clearActiveFilter: clearActiveCollectionFilter,
    // Rev160: setShareToken — memoizea server-POST-svar via proper
    // saveToStorage → server-PUT, så metafield blir synkad.
    setShareToken,
    addPlateToCollection,
    removePlateFromCollection,
    isPlateInCollection,
    // Rev213: trim per (collection, handle) — sätts via popup-flödet
    // (openCollectionPopup(handle, { trim: { in_s, out_s } })), läses av
    // collections-page + shared-collection för range-badges.
    setPlateTrim,
    getPlateTrim,
    clearPlateTrim,
    formatTrimTime,
    // Rev145: shared-plate-set lookup (för feed.js userFilterMatches)
    sharedPlatesIncludes,
    // Rev214: ägarens trim för en handle i aktiv shared-kontext
    // (anonym-tillgänglig — läses av product.js hydrate-chain)
    getSharedTrim,
    // Rev183: shared-collection meta (name + count) för filter-rubrik
    getSharedMeta,
    // Diagnostic helper
    debug: () => ({
      count: collections.size,
      collections: getCollections(),
      max: MAX_COLLECTIONS,
      shared_plates_count: _sharedPlates.size,
    }),
  };

  /*
    Rev151: använd `load`-event istället för DOMContentLoaded. Garanterar
    att alla defer-scripts (inkl feed.js) har körts och lyssnare är
    attach:ade. Tidigare dispatchades filter-event för tidigt → feed.js
    missade det → filter applicerades inte.
  */
  if (document.readyState === 'complete') {
    applyUrlStateFilter();
  } else {
    window.addEventListener('load', applyUrlStateFilter, { once: true });
  }
})();
